import React, { useEffect, useRef, useState } from "react";

/**
 * Lightweight autocomplete: typeahead with dropdown of suggestions.
 * Free text is allowed — user can type a value that isn't in the list.
 * Props:
 *  - value, onChange (string)
 *  - fetcher: async (q) => array of strings or objects with `label` & optional `meta`
 *  - placeholder, testid, onSelect (called when picking an existing suggestion)
 */
export default function Autocomplete({ value, onChange, fetcher, placeholder, testid, onSelect, getLabel = (i) => (typeof i === "string" ? i : i.label) }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const triggerFetch = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await fetcher(q);
        setItems(list || []);
      } catch (e) {
        setItems([]);
      }
    }, 150);
  };

  const handleFocus = () => {
    setOpen(true);
    triggerFetch(value || "");
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
    setActive(-1);
    triggerFetch(v);
  };

  const handleSelect = (item) => {
    const label = getLabel(item);
    onChange(label);
    setOpen(false);
    if (onSelect) onSelect(item);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(-1, a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); handleSelect(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        className="brut-input"
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKey}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border-2 border-black brut-shadow max-h-60 overflow-auto"
             data-testid={`${testid}-dropdown`}>
          {items.map((item, i) => {
            const label = getLabel(item);
            const meta = typeof item === "object" ? (item.meta || "") : "";
            return (
              <div
                key={`${label}-${i}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
                onMouseEnter={() => setActive(i)}
                className={`px-3 py-2 cursor-pointer border-b border-black/10 last:border-b-0
                  ${active === i ? "bg-[#E63946] text-white" : "hover:bg-[#F4F4F0]"}`}
                data-testid={`${testid}-opt-${i}`}
              >
                <div className="font-bold text-sm">{label}</div>
                {meta && <div className={`text-xs ${active === i ? "text-white/80" : "text-[#666]"} uppercase tracking-wider`}>{meta}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
